import { Level } from '../types/Level';
import { firebaseService } from '../services/firebaseService';

const STORAGE_KEY = 'tower-levels';

// Fallback to localStorage if Firebase fails
const localStorageFallback = {
  loadLevels: (): Level[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading levels from localStorage:', error);
      return [];
    }
  },

  saveLevels: (levels: Level[]): void => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(levels));
    } catch (error) {
      console.error('Error saving levels to localStorage:', error);
    }
  }
};

// Firebase-first storage with localStorage fallback
export const loadLevels = async (): Promise<Level[]> => {
  try {
    return await firebaseService.getLevels();
  } catch (error) {
    console.warn('Firebase failed, falling back to localStorage:', error);
    return localStorageFallback.loadLevels();
  }
};

export const addLevel = async (level: Omit<Level, 'id'>): Promise<Level> => {
  try {
    const newLevel = await firebaseService.addLevel(level);
    
    // Also save to localStorage as backup
    try {
      const localLevels = localStorageFallback.loadLevels();
      localLevels.push(newLevel);
      localStorageFallback.saveLevels(localLevels);
    } catch (localError) {
      console.warn('Failed to save to localStorage backup:', localError);
    }
    
    return newLevel;
  } catch (error) {
    console.warn('Firebase failed, falling back to localStorage:', error);
    
    // Fallback to localStorage
    const localLevels = localStorageFallback.loadLevels();
    
    // Generate next numeric ID for localStorage fallback too
    let nextId = 1;
    if (localLevels.length > 0) {
      const highestId = Math.max(...localLevels.map(level => parseInt(level.id) || 0));
      nextId = highestId + 1;
    }
    
    const newLevel: Level = {
      ...level,
      id: nextId.toString()
    };
    localLevels.push(newLevel);
    localStorageFallback.saveLevels(localLevels);
    return newLevel;
  }
};

export const updateLevel = async (id: string, updates: Partial<Level>): Promise<Level | null> => {
  try {
    const updatedLevel = await firebaseService.updateLevel(id, updates);
    
    // Also update localStorage backup
    try {
      const localLevels = localStorageFallback.loadLevels();
      const index = localLevels.findIndex(level => level.id === id);
      if (index !== -1) {
        localLevels[index] = updatedLevel;
        localStorageFallback.saveLevels(localLevels);
      }
    } catch (localError) {
      console.warn('Failed to update localStorage backup:', localError);
    }
    
    return updatedLevel;
  } catch (error) {
    console.warn('Firebase failed, falling back to localStorage:', error);
    
    // Fallback to localStorage
    const localLevels = localStorageFallback.loadLevels();
    const index = localLevels.findIndex(level => level.id === id);
    if (index === -1) return null;
    
    localLevels[index] = {
      ...localLevels[index],
      ...updates
    };
    localStorageFallback.saveLevels(localLevels);
    return localLevels[index];
  }
};

export const deleteLevel = async (id: string): Promise<boolean> => {
  try {
    const success = await firebaseService.deleteLevel(id);
    
    // Also remove from localStorage backup
    try {
      const localLevels = localStorageFallback.loadLevels();
      const filteredLevels = localLevels.filter(level => level.id !== id);
      localStorageFallback.saveLevels(filteredLevels);
    } catch (localError) {
      console.warn('Failed to update localStorage backup:', localError);
    }
    
    return success;
  } catch (error) {
    console.warn('Firebase failed, falling back to localStorage:', error);
    
    // Fallback to localStorage
    const localLevels = localStorageFallback.loadLevels();
    const filteredLevels = localLevels.filter(level => level.id !== id);
    if (filteredLevels.length === localLevels.length) return false;
    
    localStorageFallback.saveLevels(filteredLevels);
    return true;
  }
};

export const addLevelWithId = async (id: string, levelData: Omit<Level, 'id'>): Promise<Level> => {
  try {
    return await firebaseService.addLevelWithId(id, levelData);
  } catch (error) {
    console.warn('Firebase failed, falling back to localStorage:', error);
    const localLevels = localStorageFallback.loadLevels();
    const newLevel: Level = {
      ...levelData,
      id
    };
    localLevels.push(newLevel);
    localStorageFallback.saveLevels(localLevels);
    return newLevel;
  }
};

export const changeLevelNumber = async (oldId: string, newId: string, levelData: Omit<Level, 'id'>): Promise<Level> => {
  try {
    // Create new level with new ID
    const newLevel = await firebaseService.addLevelWithId(newId, levelData);
    
    // Delete old level if it exists
    if (oldId) {
      await firebaseService.deleteLevel(oldId);
    }
    
    // Update localStorage backup
    try {
      const localLevels = localStorageFallback.loadLevels();
      const filteredLevels = oldId ? localLevels.filter(level => level.id !== oldId) : localLevels;
      filteredLevels.push(newLevel);
      localStorageFallback.saveLevels(filteredLevels);
    } catch (localError) {
      console.warn('Failed to update localStorage backup:', localError);
    }
    
    return newLevel;
  } catch (error) {
    console.warn('Firebase failed, falling back to localStorage:', error);
    
    // Fallback to localStorage
    const localLevels = localStorageFallback.loadLevels();
    const filteredLevels = oldId ? localLevels.filter(level => level.id !== oldId) : localLevels;
    const newLevel: Level = {
      ...levelData,
      id: newId
    };
    filteredLevels.push(newLevel);
    localStorageFallback.saveLevels(filteredLevels);
    return newLevel;
  }
};

export const exportLevelsAsJSON = async (): Promise<string> => {
  try {
    return await firebaseService.exportLevelsAsJSON();
  } catch (error) {
    console.warn('Firebase failed, falling back to localStorage:', error);
    const localLevels = localStorageFallback.loadLevels();
    return JSON.stringify(localLevels, null, 2);
  }
};
